import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import eyeOpen from "../../assets/images/eye-open.svg";
import eyeClose from "../../assets/images/eye-close.svg";

const EyeComponent = ({ image }) => (
  <span
    style={{
      cursor: "pointer",
      marginLeft: "5px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      filter: "brightness(0) invert(1)",
    }}
  >
    <img src={image} alt="eye" style={{ width: 16, height: 16 }} />
  </span>
);

export default function CardDetailsHorizontal({
  cardData,
  userData: userDataProp, // Accept userData from parent
  sensitiveData,
  isAdmin,
}) {
  const [visibleField, setVisibleField] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  const userDataRedux = useSelector(
    (state) => state.cards.profile?.data?.response?.profile
  );

  const userData = userDataProp || userDataRedux;

  useEffect(() => {
    if (!imageSrc && userData?.cards?.data?.imageUrl) {
      setImageSrc(userData.cards.data.imageUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.cards?.data?.imageUrl]);

  useEffect(() => {
    const imageUrl =
      imageSrc ||
      userData?.cards?.data?.imageUrl ||
      cardData?.imageUrl ||
      cardData?.data?.imageUrl;

    if (!imageUrl) {
      setImgError(true);
      setImgLoading(false);
      return;
    }

    setImgLoading(true);
    const img = new Image();

    img.onload = () => {
      setImgLoading(false);
      setImgError(false);
    };

    img.onerror = () => {
      setImgLoading(false);
      setImgError(true);
    };

    img.src = imageUrl;
  }, [imageSrc, userData?.cards?.data?.imageUrl, cardData?.imageUrl, cardData?.data?.imageUrl]);

  const formatNumber = (number = "") =>
    number.replace(/(.{4})/g, "$1 ").trim();

  const maskCardNumber = (num) =>
    num ? `XXXX XXXX XXXX ${num.slice(-4)}` : "-";

  const maskExpiryDate = (date) => (date ? "XX/XX" : "");

  const maskCVV = (cvv) => (cvv ? "XXX" : "");

  const formatExpiryDate = (date) =>
    date ? `${date.slice(0, 2)}/${date.slice(-2)}` : "";

  return (
    <div
      className="debit-card"
      style={{
        position: "relative",
        backgroundImage: !imgError && !imgLoading ? `url(${imageSrc || userData?.cards?.data?.imageUrl || cardData?.imageUrl || cardData?.data?.imageUrl})` : "none",
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      {imgLoading && (
        <div
          className="card-image-placeholder"
          aria-hidden
          style={{ position: "absolute", inset: 0 }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "#f5f5f6",
              borderRadius: 8,
            }}
          />
        </div>
      )}

      {imgError && (
        <div
          className="card-image-fallback"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#999",
            borderRadius: 5,
            background: "#f5f5f6",
          }}
        >
          Card image unavailable
        </div>
      )}

      <div style={{ marginTop: "2.2rem", textAlign: "start" }}>
        <span
          style={{
            color: "#fff",
            background: "transparent",
            borderRadius: "2px",
            padding: "0.3rem 0.5rem",
            fontSize: "0.9rem",
          }}
        >
          {cardData?.status}
        </span>
      </div>
      <div className="mt1 ref">
        {/* Card Number with Eye Icon */}
        <span
          className="profileCardRightSideCardNumber"
          style={{
            display: "block",
            textAlign: "left",
            color: "#fff",
          }}
        >
          {visibleField === "cardNumber"
            ? formatNumber(sensitiveData?.response?.cardNumber)
            : maskCardNumber(sensitiveData?.response?.cardNumber || "-")}
          <span
            onClick={() =>
              setVisibleField(
                visibleField === "cardNumber" ? null : "cardNumber"
              )
            }
            style={{
              cursor: "pointer",
              marginLeft: "5px",
              color: "#fff",
            }}
          >
            {!isAdmin ? (
              visibleField === "cardNumber" ? (
                <EyeComponent image={eyeOpen} />
              ) : (
                <EyeComponent image={eyeClose} />
              )
            ) : (
              ""
            )}
          </span>
        </span>
        <div className="d-flex" style={{ gap: "0.5rem" }}>
          {/* Expiry Date with Eye Icon */}
          <div className="expiryDate v-center">
            <span className="label-text">
              VALID <br /> UPTO
            </span>
            <span style={{ fontSize: "0.9rem", color: "#fff" }}>
              {visibleField === "expiryDate"
                ? formatExpiryDate(sensitiveData?.response?.expiryDate)
                : maskExpiryDate(sensitiveData?.response?.expiryDate)}
              <span
                onClick={() =>
                  setVisibleField(
                    visibleField === "expiryDate" ? null : "expiryDate"
                  )
                }
                style={{
                  cursor: "pointer",
                  marginLeft: "5px",
                  color: "#fff",
                }}
              >
                {!isAdmin ? (
                  visibleField === "expiryDate" ? (
                    <EyeComponent image={eyeOpen} />
                  ) : (
                    <EyeComponent image={eyeClose} />
                  )
                ) : (
                  ""
                )}
              </span>
            </span>
          </div>

          {/* CVV with Eye Icon */}
          <div className="cvv v-center">
            <span className="label-text">CVV</span>
            <span style={{ fontSize: "0.9rem", color: "#fff" }}>
              {visibleField === "cvv"
                ? sensitiveData?.response?.cvv
                : maskCVV(sensitiveData?.response?.cvv)}
              <span
                onClick={() =>
                  setVisibleField(visibleField === "cvv" ? null : "cvv")
                }
                style={{
                  cursor: "pointer",
                  marginLeft: "5px",
                  color: "#fff",
                }}
              >
                {!isAdmin ? (
                  visibleField === "cvv" ? (
                    <EyeComponent image={eyeOpen} />
                  ) : (
                    <EyeComponent image={eyeClose} />
                  )
                ) : (
                  ""
                )}
              </span>
            </span>
          </div>
        </div>
        <div style={{ textAlign: "left", paddingBottom: "0.5rem" }}>
          <span style={{ color: "#fff", fontSize: "0.9rem" }}>
            {cardData?.employee_details?.name?.length > 25
              ? `${cardData?.employee_details?.name.substring(0, 25)}...`
              : cardData?.employee_details?.name}
          </span>
        </div>
      </div>
    </div>
  );
}
